import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import type { ApiResponse, StartSuccessFeeMigrationResult } from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

type StartSuccessFeeMigrationResponse = ApiResponse<StartSuccessFeeMigrationResult>;

/** Lets the success toast render before the page leaves for Shopify. */
const REDIRECT_DELAY_MS = 500;

/**
 * Ask Shopify for a replacement subscription carrying the success-fee usage line.
 *
 * The call is idempotent: a pending attempt younger than 48h answers 200 with the
 * same approval page, so retrying never creates a second charge.
 */
const startSuccessFeeMigration = async (): Promise<StartSuccessFeeMigrationResponse> => {
  const response = await api.post<StartSuccessFeeMigrationResponse>(
    '/dashboard/billing/success-fee-migration'
  );

  if (!response.data.success || !response.data.data) {
    // A refusal normally arrives as a rejected axios call, whose error reaches
    // `getApiErrorMessage` untouched. This branch is the rare 2xx body that
    // still says no, so it carries the same `response` shape rather than a bare
    // Error, or the `errors.<CODE>` lookup would never match.
    throw Object.assign(
      new Error(response.data.message || 'Failed to start the success fee migration'),
      { response }
    );
  }

  return response.data;
};

/**
 * Hook starting the success-fee billing migration and sending the merchant to the
 * Shopify approval page, the same redirect pattern as
 * `use-change-subscription-plan.ts`.
 */
export function useStartSuccessFeeMigration() {
  const { t } = useTranslation();

  return useMutation<StartSuccessFeeMigrationResponse, Error, void>({
    mutationFn: startSuccessFeeMigration,
    onSuccess: (response) => {
      const confirmationUrl = response.data?.confirmationUrl;
      if (!confirmationUrl) return;

      toaster.success({
        title: t('settings.subscription.successFeeMigration.start.success.title'),
        description: t('settings.subscription.successFeeMigration.start.success.description'),
      });

      setTimeout(() => {
        window.location.href = confirmationUrl;
      }, REDIRECT_DELAY_MS);
    },
    onError: (error) => {
      toaster.error({
        title: t('settings.subscription.successFeeMigration.start.error'),
        description: getApiErrorMessage(
          error,
          t,
          'settings.subscription.successFeeMigration.start.error'
        ),
      });
    },
  });
}
